import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'hooks/useStore';
import { OOCStatus, OOCSubmission, OOCTabType } from '../oocForecast.type';
import { isCurrentUser } from 'utils/userUtils';

export const CONTRACT_TYPE_OPTIONS: { label: string; value: OOCTabType | 'all' }[] = [
  { label: 'All Contract Types', value: 'all' },
  { label: 'MAS DP', value: 'MAS' },
  { label: 'NC DP', value: 'NC' },
  { label: 'Prov. MAS', value: 'ProvMAS' },
  { label: 'Prov. NC', value: 'ProvNC' },
];

export type SummaryCardConfig = {
  key: OOCStatus | 'all' | 'history';
  label: string;
  count: number;
  iconType: 'fileText' | 'mail' | 'clock';
  colorClass: 'blue' | 'gray';
};

export const getStatusBadgeClass = (status: OOCStatus, styles: Record<string, string>): string => {
  switch (status) {
    case 'draft':
      return styles.draft;
    case 'communicated':
      return styles.communicated;
    case 'integrated':
      return styles.integrated;
    default:
      return '';
  }
};

export const getStatusLabel = (status: OOCStatus): string => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'communicated':
      return 'Submitted / Communicated';
    case 'integrated':
      return 'Integrated';
    default:
      return status;
  }
};

const _currentPlanningPeriod = (): string => {
  const now = new Date();
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[now.getMonth()]} ${now.getFullYear()}`;
};

export const useOOCDashboardLogic = () => {
  const { OOCStore, UserStore } = useStore();
  const loggedInUserName = UserStore.userMetadata?.userInfo?.name ?? '';
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    OOCStore.fetchSubmissions();
  }, [OOCStore]);

  const handleCardClick = useCallback(
    (filter: OOCStatus | 'all' | 'history') => {
      if (filter === 'history') {
        OOCStore.setScreen('history');
        return;
      }
      OOCStore.setActiveCardFilter(filter);
    },
    [OOCStore]
  );

  const handleCreateNew = useCallback(() => {
    OOCStore.createNewSubmission(loggedInUserName, _currentPlanningPeriod());
  }, [OOCStore, loggedInUserName]);

  // Called by AutoComplete's completeMethod on each keystroke (after built-in delay).
  const handleSuggestionComplete = useCallback(
    (query: string) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      OOCStore.fetchSuggestions(query, loggedInUserName, abortControllerRef.current.signal);
    },
    [OOCStore, loggedInUserName]
  );

  // Execute the full search (called on Enter or when a suggestion is selected).
  const handleSearch = useCallback(() => {
    OOCStore.performSearch(loggedInUserName);
  }, [OOCStore, loggedInUserName]);

  // Called when the user selects a suggestion from the dropdown.
  const handleSuggestionSelect = useCallback(
    (value: string) => {
      OOCStore.setSearchQuery(value);
      OOCStore.performSearch(loggedInUserName);
    },
    [OOCStore, loggedInUserName]
  );

  // When a backend search is active, show those results directly.
  // Otherwise fall back to the normal filtered list (scoped by draft ownership).
  const displayedSubmissions: OOCSubmission[] =
    OOCStore.searchResults !== null
      ? OOCStore.searchResults
      : OOCStore.filteredSubmissions.filter(
          (sub) => sub.status !== 'draft' || isCurrentUser(loggedInUserName, sub.submittedBy)
        );

  const myDraftCount = OOCStore.submissions.filter(
    (sub) => sub.status === 'draft' && isCurrentUser(loggedInUserName, sub.submittedBy)
  ).length;

  const summaryCardConfigs: SummaryCardConfig[] = [
    {
      key: 'all',
      label: 'All Active',
      count: OOCStore.dashboardCounts.all,
      iconType: 'fileText',
      colorClass: 'blue',
    },
    {
      key: 'draft',
      label: 'Drafts',
      count: myDraftCount,
      iconType: 'fileText',
      colorClass: 'blue',
    },
    {
      key: 'communicated',
      label: 'Submitted / Communicated',
      count: OOCStore.dashboardCounts.communicated,
      iconType: 'mail',
      colorClass: 'blue',
    },
    {
      key: 'history',
      label: 'History',
      count: OOCStore.dashboardCounts.history,
      iconType: 'clock',
      colorClass: 'gray',
    },
  ];

  return {
    OOCStore,
    loggedInUserName,
    displayedSubmissions,
    handleCardClick,
    handleCreateNew,
    handleSearch,
    handleSuggestionComplete,
    handleSuggestionSelect,
    summaryCardConfigs,
  };
};
